import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { Button } from 'ui-components/components/button';

import ShipFromModal from '../../../../../../../ship-from/modals';
import { trackShipFromConfigStart } from '../../../../../../../ship-from/tracking-events';
import useFetchShipFroms from '../../../../../../settings/ship-from-locations/use-fetch-ship-froms';
import { useModal } from '../../../../../../../hooks/use-modal';

import messages from '../messages';
import style from './style.module.less';
import { useImportContext } from '../../../../../../../components/csv-import/ui/components/import-context';
import { getTheNewestWarehouse } from '../../../context/product-import-context/utils';
import { SET_INVENTORY_WAREHOUSE } from '../../../context/product-import-context/reducer';

const SHIP_FROM_WIZARD_STEP = 'shipFromWizardStep';

/**
 * @param {object} props - Component props
 * @param {() => void} [props.onSuccess] - Callback function called when a new ship-from location is successfully added
 * @param {object} [props.buttonText] - Message object for the button text (defaults to addAnotherShipFrom)
 * @param {boolean} [props.showIcon] - Whether to show the plus icon (defaults to true)
 * @param {string} [props.className] - Additional CSS class for the container
 * @returns {React.ReactElement} A button that opens the ship-from modal when clicked
 */
export const AddShipFromLink = ({ onSuccess, buttonText = messages.addAnotherShipFrom, showIcon = true, className }) => {
  const { open, close, isOpen } = useModal();
  const { fetchShipFroms } = useFetchShipFroms();

  const { dispatch } = useImportContext();

  const onAddShipFromSuccess = useCallback(
    async (_, updatedWarehouses) => {
      try {
        await fetchShipFroms();
        const theNewestWarehouse = getTheNewestWarehouse(updatedWarehouses);
        if (theNewestWarehouse) {
          dispatch({
            type: SET_INVENTORY_WAREHOUSE,
            inventoryWarehouseId: theNewestWarehouse.inventoryWarehouseId,
          });
        }
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.addShipFromSuccessMessageTitle} />,
          content: '',
          duration: NOTIFICATION_DURATIONS.SHORT,
        });

        if (onSuccess) {
          onSuccess();
        }
      } catch {
        // TODO: Handle error in modal with input from design (SPD-21075). Started in slowe/error-message-sf
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.addShipFromErrorMessageTitle} />,
          content: '',
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      } finally {
        close();
      }
    },
    [fetchShipFroms, onSuccess, dispatch, close],
  );

  const onClick = useCallback(() => {
    open();
    trackShipFromConfigStart(SHIP_FROM_WIZARD_STEP);
  }, [open]);

  return (
    <div className={className || style['add-link-container']}>
      <Button className={style['add-another-link']} onClick={onClick} theme='link'>
        {showIcon && <FontAwesomeIcon className={style['plus-icon']} icon={faPlus} />}
        <FormattedMessage className={style['add-another-link-text']} {...buttonText} />
      </Button>
      {isOpen && (
        <ShipFromModal onClose={close} onSuccess={onAddShipFromSuccess} shouldCreateInventoryWarehouse source={SHIP_FROM_WIZARD_STEP} />
      )}
    </div>
  );
};
